import { useMatchNotification } from '@services/banking/mutations/useMatchNotification'
import { useNotificationCandidates } from '@services/banking/queries/useNotificationCandidates'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'

import type { BankNotification } from '../types'

interface Props {
  notification: BankNotification | null
  onClose: () => void
}

export function MatchNotificationDialog({ notification, onClose }: Props) {
  const { t } = useTranslation('banking')
  const [selected, setSelected] = useState<string | null>(null)
  const candidates = useNotificationCandidates(notification?.id)
  const match = useMatchNotification()

  // Pri otvorení inej platby nesmie ostať predvybraný predpis z tej predošlej.
  useEffect(() => setSelected(null), [notification?.id])

  const confirm = () => {
    if (!notification || !selected) return
    match.mutate(
      { id: notification.id, rentId: selected },
      { onSuccess: onClose }
    )
  }

  const rows = candidates.data ?? []

  return (
    <Dialog open={!!notification} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('match.title')}</DialogTitle>
        </DialogHeader>

        {notification && (
          <div className="rounded-md bg-ink/[0.04] p-3 text-sm">
            <div className="font-medium text-ink">
              {notification.amount} {notification.currency}
            </div>
            <div className="text-xs text-ink/60">
              {t('match.reference')}: {notification.reference || '—'}
              {notification.counterparty_name
                ? ` · ${notification.counterparty_name}`
                : ''}
            </div>
          </div>
        )}

        <div className="max-h-72 space-y-1 overflow-y-auto">
          {candidates.isPending && (
            <p className="p-2 text-sm text-ink/50">{t('match.loading')}</p>
          )}
          {!candidates.isPending && rows.length === 0 && (
            <p className="p-2 text-sm text-ink/50">{t('match.empty')}</p>
          )}
          {rows.map((rent) => (
            <button
              key={rent.id}
              type="button"
              onClick={() => setSelected(rent.id)}
              className={`block w-full rounded px-3 py-2 text-left text-sm transition-colors ${
                selected === rent.id ? 'bg-ink/[0.08]' : 'hover:bg-ink/[0.04]'
              }`}
            >
              <div className="flex justify-between gap-3">
                <span className="font-medium text-ink">{rent.label || '—'}</span>
                <span className="shrink-0 text-ink/70">
                  {rent.amount.toFixed(2)} EUR
                </span>
              </div>
              <div className="text-xs text-ink/50">
                VS {rent.variable_symbol} · ŠS {rent.specific_symbol || '—'} ·{' '}
                {new Date(rent.due_date).toLocaleDateString('sk-SK')}
              </div>
            </button>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button onClick={confirm} disabled={!selected || match.isPending}>
            {t('match.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
